import enterpriseApi from "api/enterprise";
import {IAccountant} from "api/types";
import {ThankYouCheckIcon} from "assets/icons";
import Modal from "components/Modal";
import ModalLayout from "components/ModalLayout";
import Loader from "components/loaders/Loader";
import {handleError} from "helpers/errors";
import {AwaitableModal} from "hooks/useAwaitableModal";
import {useMemo, useState} from "react";

const NewEmployeeModal: AwaitableModal<IAccountant | undefined> = ({open, resolve}) => {
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [created, setCreated] = useState<IAccountant>();
  const [submitting, setSubmitting] = useState(false);

  const signInLink = useMemo(() => `https://fastandeasyqbo.booksmatch.us/auth/code/${created?.magic}`, [created]);

  const emailValid = email?.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g);

  const onCreate = () => {
    if (submitting) return;
    if (!email) return handleError("Please enter the email");
    if (!emailValid) return handleError("Please enter a valid email");
    if (!firstName) return handleError("Please enter the first name");
    if (!lastName) return handleError("Please enter the last name");
    if (!role) return handleError("Please enter the company role");
    setSubmitting(true);
    enterpriseApi
      .createEmployee({email, firstName, lastName, role})
      .then((user) => setCreated(user))
      .finally(() => setSubmitting(false));
  };

  const onResolve = () => resolve(created);

  if (created) {
    return (
      <Modal open={open} close={onResolve}>
        <ModalLayout
          title="Employee Added"
          footer={
            <button onClick={onResolve} className="btn-primary ml-auto">
              Done
            </button>
          }
        >
          <div className="flex flex-col items-center gap-3 py-2">
            <ThankYouCheckIcon className="h-24" />
            <p className="text-sm text-brand-muted text-center">
              Your employee has been added. Send the setup link below to them.
            </p>
          </div>
          <input type="text" value={signInLink} readOnly className="w-full" />
        </ModalLayout>
      </Modal>
    );
  }

  return (
    <Modal open={open} close={!submitting ? onResolve : () => {}}>
      <ModalLayout
        title="Add an Employee"
        footer={
          <>
            <button className="btn-secondary" disabled={submitting} onClick={!submitting ? onResolve : () => {}}>
              Cancel
            </button>
            <button className="btn-primary" disabled={submitting} onClick={onCreate}>
              Create
            </button>
          </>
        }
      >
        {submitting && (
          <div className="flex flex-col items-center gap-3 py-4">
            <Loader />
            <p className="text-sm text-brand-muted text-center">Creating account, please wait…</p>
          </div>
        )}
        {!submitting && (
          <>
            <input
              type="text"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <div className="flex gap-3">
              <input
                type="text"
                className="grow"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
              />
              <input
                type="text"
                className="grow"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
              />
            </div>
            <input
              type="text"
              placeholder="Company Role"
              value={role}
              onChange={(e) => setRole(e.currentTarget.value)}
            />
          </>
        )}
      </ModalLayout>
    </Modal>
  );
};

export default NewEmployeeModal;
